import { useState } from 'react';

/** Messages de repli, quand le navigateur ne laisse pas dicter. */
export const MESSAGES_DICTEE = {
  absente:
    '🎤 La dictée vocale n’est pas disponible sur ce navigateur.\n\n' +
    'Astuce : utilise le micro 🎤 du clavier de ton téléphone — ça marche partout, tout de suite.',
  refusee:
    '🎤 Dictée vocale indisponible ici.\n\n' +
    'Le navigateur bloque l’accès au micro : autorisez-le dans les réglages du site, puis réessayez.\n\n' +
    'En attendant : utilise le micro 🎤 du clavier de ton téléphone dans le champ, ça fonctionne déjà.',
  bloquee:
    '🎤 Dictée vocale bloquée sur cet appareil.\n\n' +
    'Astuce immédiate : appuie dans le champ de texte, puis touche le micro 🎤 de ton clavier pour dicter.',
};

/**
 * Bouton « 🎤 Dicter à la voix » de l'assistant devis.
 *
 * Sur le chantier, les mains sont prises ou sales : dicter « démolition 25,
 * chape 25 » va plus vite que taper. La reconnaissance vocale du navigateur
 * suffit, en français de Suisse ; elle n'existe pas partout, et le micro peut
 * être refusé — le bouton dit alors comment dicter quand même, par le micro
 * du clavier du téléphone.
 *
 * @param {object} props propriétés du bouton
 * @param {(texte: string, propositions: string[]) => void} props.onTexte reçoit la
 *   phrase reconnue, puis toutes celles que le téléphone propose (la plus probable d'abord)
 * @returns {import('react').ReactElement} le bouton
 */
export function BoutonDictee({ onTexte }) {
  const [ecoute, setEcoute] = useState(false);

  const dicte = () => {
    try {
      const Reconnaissance = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!Reconnaissance) {
        window.alert(MESSAGES_DICTEE.absente);
        return;
      }
      const r = new Reconnaissance();
      r.lang = 'fr-CH';
      r.interimResults = false;
      // Le téléphone hésite souvent entre plusieurs phrases : on les demande
      // toutes, et l'assistant garde la plus juste (mots du métier).
      r.maxAlternatives = 5;
      r.onresult = (ev) => {
        const resultat = ev.results?.[0];
        const propositions = [];
        for (let i = 0; i < (resultat?.length || 0); i += 1) {
          const t = resultat[i]?.transcript || '';
          if (t) propositions.push(t);
        }
        if (propositions.length) onTexte(propositions[0], propositions);
      };
      r.onerror = () => {
        setEcoute(false);
        window.alert(MESSAGES_DICTEE.refusee);
      };
      r.onend = () => setEcoute(false);
      setEcoute(true);
      r.start();
    } catch {
      setEcoute(false);
      window.alert(MESSAGES_DICTEE.bloquee);
    }
  };

  return (
    <button
      type="button"
      onClick={dicte}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        width: '100%',
        background: '#fff',
        border: '1px solid #c7d2fe',
        borderRadius: 9,
        padding: 10,
        marginBottom: 8,
        cursor: 'pointer',
        fontSize: 11,
        fontWeight: 800,
        color: '#4f46e5',
      }}
    >
      {ecoute ? '🎤 À l’écoute…' : '🎤 Dicter à la voix'}
    </button>
  );
}
